/**
 * AuthContext - Global authentication state management
 * Provides authentication state, user data, and auth actions throughout the app
 * Integrates with AuthService for actual authentication operations
 */

import { createContext, useContext, useReducer, useEffect, useCallback, type ReactNode } from 'react';
import { authService, type User, type AuthTokens } from '../services/authService';
import { UserDataManager } from '../utils/tokenManager';
import { logger } from '../utils/logger';

// Types
export interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

export interface AuthContextType {
  state: AuthState;
  login: (method: 'google' | 'credentials', credentials?: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  clearError: () => void;
  refreshAuthFromStorage: () => void;
}

// Actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean };

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
        error: action.payload,
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
      };

    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    default:
      return state;
  }
}

// Create context
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        const token = authService.getToken();
        const user = authService.getCurrentUser();
        const hasValidToken = authService.isAuthenticated();

        if (hasValidToken && token && user) {
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user, token },
          });
          return;
        }

        const refreshToken = authService.getRefreshToken?.();

        if (refreshToken) {
          try {
            await authService.refreshToken();
            const refreshedToken = authService.getToken();
            const refreshedUser = authService.getCurrentUser();

            if (refreshedToken && refreshedUser) {
              dispatch({
                type: 'AUTH_SUCCESS',
                payload: { user: refreshedUser, token: refreshedToken },
              });
              return;
            }
          } catch (refreshError) {
            logger.child('AUTH').error('Token refresh during auth initialization failed:', refreshError);
          }
        }

        if (token || user) {
          await authService.logout();
        }

        dispatch({ type: 'AUTH_LOGOUT' });
      } catch (error) {
        logger.child('AUTH').error('Auth initialization failed:', error);
        dispatch({ type: 'AUTH_LOGOUT' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();

    // Listen for session expiration events from apiService
    const handleSessionExpired = () => {
      logger.child('AUTH').warn('Session expired event received');
      dispatch({ type: 'AUTH_LOGOUT' });
      // Redirect to login page
      window.location.href = '/login';
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  // Login function
  const login = async (
    method: 'google' | 'credentials',
    credentials?: { email: string; password: string }
  ) => {
    try {
      dispatch({ type: 'AUTH_START' });

      let result: { user: User; tokens: AuthTokens };

      if (method === 'google') {
        result = await authService.loginWithFirebase();
      } else if (method === 'credentials' && credentials) {
        result = await authService.loginWithCredentials(credentials.email, credentials.password);
      } else {
        throw new Error('Invalid login method or missing credentials');
      }

      // Store user data using UserDataManager for persistence
      UserDataManager.storeUser(result.user);

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: result.user,
          token: result.tokens.token,
        },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({
        type: 'AUTH_FAILURE',
        payload: errorMessage,
      });
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await authService.logout();
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      logger.child('AUTH').error('Logout failed:', error);
      // Force logout even if service call fails
      dispatch({ type: 'AUTH_LOGOUT' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Update user function
  const updateUser = (user: User) => {
    UserDataManager.storeUser(user);
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const refreshAuthFromStorage = useCallback(() => {
    const token = authService.getToken();
    const user = authService.getCurrentUser();

    if (token && user) {
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user,
          token,
        },
      });
    } else {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const contextValue: AuthContextType = {
    state,
    login,
    logout,
    updateUser,
    clearError,
    refreshAuthFromStorage,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Custom hook for auth state only
export function useAuthState(): AuthState {
  const { state } = useAuth();
  return state;
}